import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import type { GtmBoardData, GtmContentData, GtmHealthData, GtmMarketingData } from '../../../api/gtm';

/**
 * The board and performance endpoints fan out to many live sources. The API
 * caps each source at 15s and returns partial data, so anything past this
 * ceiling is a real failure: show the error with Retry instead of a spinner
 * that sits for minutes (the global client timeout is 120s, ×2 with a retry).
 */
const FAN_OUT_TIMEOUT_MS = 45_000;

async function getWithTimeout<T>(endpoint: string, params: Record<string, string | undefined>): Promise<T> {
  try {
    return await apiClient.get<T>(endpoint, { params, signal: AbortSignal.timeout(FAN_OUT_TIMEOUT_MS) });
  } catch (err) {
    if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
      throw Object.assign(new Error(`No answer after ${FAN_OUT_TIMEOUT_MS / 1000}s: one of the sources is not responding.`), { cause: err });
    }
    throw err;
  }
}

const fanOutQueryOptions = {
  retry: false,
  refetchInterval: 300_000,
  refetchOnWindowFocus: false,
} as const;

export function useBoardQuery(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['gtm', 'board', startDate, endDate],
    queryFn: () => getWithTimeout<GtmBoardData>('/gtm/board', { startDate, endDate }),
    ...fanOutQueryOptions,
  });
}

export function usePerformanceQuery(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['gtm', 'marketing', startDate, endDate],
    queryFn: () => getWithTimeout<GtmMarketingData>('/gtm/marketing', { startDate, endDate }),
    ...fanOutQueryOptions,
  });
}

export function useOpportunitiesQuery(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['gtm', 'content', startDate, endDate],
    queryFn: () => getWithTimeout<GtmContentData>('/gtm/content', { startDate, endDate }),
    ...fanOutQueryOptions,
  });
}

export function useHealthQuery(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['gtm', 'health', startDate, endDate],
    queryFn: () => getWithTimeout<GtmHealthData>('/gtm/health', { startDate, endDate }),
    ...fanOutQueryOptions,
  });
}

/** Section keys the API reports in `unavailable`, in words. */
const SECTION_LABELS: Record<string, string> = {
  gscClient: 'Search Console',
  searchTrend: 'Organic search trend',
  brandSplit: 'Brand vs category',
  shareOfSearch: 'Share of search',
  authority: 'AI authority',
  sourcedPipeline: 'Sourced pipeline',
  icpByVertical: 'ICP-fit by vertical',
  markets: 'Markets',
  searchScore: 'Search scorecards',
  pipelineScore: 'Pipeline scorecard',
  demoScore: 'Demo scorecard',
  demoFunnel: 'Demo funnel',
  fixQueue: 'Title/meta fix queue',
  queryMovement: 'Query movement',
  buyerIntent: 'G2 buyer intent',
  linkedinOrganic: 'LinkedIn organic',
  marketConversion: 'Market conversion',
  emailPerformance: 'Email performance',
  paidEfficiency: 'Paid efficiency',
  linkedinCampaigns: 'LinkedIn campaign lookup (lead forms → campaigns)',
  attribution: 'Form submissions by UTM',
  leads: 'Leads (forms)',
  sessions: 'GA4 sessions',
  leadFunnel: 'Lead funnel',
  clusterCoverage: 'Cluster coverage',
  quickWins: 'Quick wins',
  contentGaps: 'Content gaps',
  glossary: 'Glossary',
  demandShifts: 'Demand shifts',
  agentVisits: 'AI-bot visits',
  indexCoverage: 'Index coverage',
  webVitals: 'Core Web Vitals',
  lighthouse: 'Lighthouse',
  siteAudit: 'Site audit',
  botAccess: 'robots.txt access',
  aiCitationGaps: 'AI citation gaps',
  citedSources: 'Cited sources',
  icpTraffic: 'ICP-fit traffic',
  contentConversion: 'Content conversion',
  backlinkTrend: 'Backlinks',
};

export function sectionLabel(key: string): string {
  return SECTION_LABELS[key] ?? key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}
